import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiBarChart2, FiCalendar, FiChevronRight } from 'react-icons/fi';

const Reports = () => {
  const { currentLang, fetchWithAuth, API_URL } = useAuth();
  const navigate = useNavigate();
  
  // Состояние для сайтов
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');

  // Состояние для периода
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() - 1; // прошлый месяц
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    return firstDay;
  });
  const [dateTo, setDateTo] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() - 1; // прошлый месяц
    const lastDay = new Date(year, month + 1, 0).getDate();
    const lastDayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    return lastDayStr;
  });

  // Загрузка сайтов при монтировании компонента
  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      setSitesLoading(true);
      setSitesError('');
      
      const response = await fetchWithAuth(`${API_URL}${currentLang}/seo/sites`);

      const data = await response.json();

      if (response.ok && data.success) {
        setSites(data.sites || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки сайтов');
      }
    } catch (error) {
      if (error.message === 'Требуется авторизация') {
        setSitesError('Сессия истекла. Пожалуйста, войдите снова.');
      } else {
        setSitesError(error.message);
      }
    } finally {
      setSitesLoading(false);
    }
  };

  const handleSiteSelect = (site) => {
    // Переходим на страницу отчета с параметрами
    navigate(`/seo/reports/${site.id}?dateFrom=${dateFrom}&dateTo=${dateTo}`);
  };

  // Функции для быстрого выбора периодов
  const setLast30Days = () => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
    setDateTo(now.toISOString().split('T')[0]);
  };

  const setCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const lastDayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    setDateFrom(firstDay);
    setDateTo(lastDayStr);
  };

  const setPreviousMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() - 1;
    const firstDay = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const lastDayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    setDateFrom(firstDay);
    setDateTo(lastDayStr);
  };

  const setCurrentQuarter = () => {
    const now = new Date();
    const year = now.getFullYear();
    const quarter = Math.floor(now.getMonth() / 3);
    const quarterStartMonth = quarter * 3;
    const quarterEndMonth = quarterStartMonth + 2;
    
    const firstDay = `${year}-${String(quarterStartMonth + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(year, quarterEndMonth + 1, 0).getDate();
    const lastDayStr = `${year}-${String(quarterEndMonth + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    
    setDateFrom(firstDay);
    setDateTo(lastDayStr);
  };

  const setCurrentYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const firstDay = `${year}-01-01`;
    const lastDay = `${year}-12-31`;
    setDateFrom(firstDay);
    setDateTo(lastDay);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Неверная дата';
    return date.toLocaleDateString('ru-RU');
  };

  return (
    <div className="mt-8 sm:mt-12 mb-4 sm:mb-8 flex flex-col gap-6 sm:gap-12">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-lg sm:shadow-xl dark:bg-navy-800">
        <div className="bg-gradient-to-r from-gray-100 to-blue-100 rounded-t-lg sm:rounded-t-xl p-4 sm:p-6 mb-4 sm:mb-8">
          <h1 className="text-lg sm:text-xl font-bold text-blue-500">
            SEO Отчеты - Выбор сайта
          </h1>
          <p className="text-gray-900 mt-1 sm:mt-2 text-sm sm:text-base">
            Выберите сайт и период для генерации SEO отчета
          </p>
        </div>
        
        <div className="p-3 sm:p-6">
          {/* Выбор периода */}
          <div className="mb-6">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <FiCalendar className="h-4 w-4" />
              Период отчета
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <input
                type="date"
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:border-gray-600 dark:text-white text-sm"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="Дата с"
              />
              
              <input
                type="date"
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-700 dark:border-gray-600 dark:text-white text-sm"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="Дата по"
              />
            </div>
            
            {/* Быстрые кнопки периодов */}
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800 text-xs sm:text-sm"
                onClick={setLast30Days}
              >
                30 дней
              </button>
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800 text-xs sm:text-sm"
                onClick={setCurrentMonth}
              >
                <span className="hidden sm:inline">Текущий месяц</span>
                <span className="sm:hidden">Месяц</span>
              </button>
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors dark:bg-orange-900 dark:text-orange-200 dark:hover:bg-orange-800 text-xs sm:text-sm"
                onClick={setPreviousMonth}
              >
                <span className="hidden sm:inline">Прошлый месяц</span>
                <span className="sm:hidden">Прошлый</span>
              </button>
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors dark:bg-purple-900 dark:text-purple-200 dark:hover:bg-purple-800 text-xs sm:text-sm"
                onClick={setCurrentQuarter}
              >
                <span className="hidden sm:inline">Текущий квартал</span>
                <span className="sm:hidden">Квартал</span>
              </button>
              <button
                className="px-2 sm:px-3 py-1.5 sm:py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors dark:bg-indigo-900 dark:text-indigo-200 dark:hover:bg-indigo-800 text-xs sm:text-sm"
                onClick={setCurrentYear}
              >
                <span className="hidden sm:inline">Текущий год</span>
                <span className="sm:hidden">Год</span>
              </button>
            </div>
            
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Выбранный период: {formatDate(dateFrom)} - {formatDate(dateTo)}
            </div>
          </div>

          {sitesError && (
            <div className="mb-4 sm:mb-6">
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded text-sm dark:bg-red-900 dark:border-red-700 dark:text-red-200">
                {sitesError}
              </div>
            </div>
          )}

          {sitesLoading ? (
            <div className="flex justify-center items-center py-6 sm:py-8">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500"></div>
              <p className="ml-2 sm:ml-3 text-gray-600 text-sm sm:text-base">Загрузка сайтов...</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4">
              {sites.map((site) => (
                <div key={site.id} className="bg-white rounded-lg shadow-md p-4 sm:p-6 border dark:bg-navy-700 dark:text-white dark:border-white/10 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => handleSiteSelect(site)}>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0 mb-3 sm:mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1 sm:mb-2 flex flex-wrap items-center gap-1 sm:gap-2">
                        <img src={`https://www.google.com/s2/favicons?domain=${site.dns}`} alt="favicon" className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate">{site.dns}</span>
                        <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-bold rounded-full flex-shrink-0 ${
                          site.https === 1 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {site.https === 1 ? 'HTTPS' : 'HTTP'}
                        </span>
                      </h3>
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                      <FiBarChart2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                      <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">SEO Отчет</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      Нажмите для генерации отчета
                    </p>
                    <FiChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              ))}
              
              {sites.length === 0 && !sitesLoading && (
                <div className="text-center py-6 sm:py-8">
                  <div className="text-gray-400 dark:text-gray-500 mb-4">
                    <FiBarChart2 className="mx-auto h-12 w-12" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Нет сайтов для отчетов
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 px-2">
                    Добавьте сайты в разделе "SEO Сайты" для генерации отчетов
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports; 